import { useEffect, useMemo, useState } from "react";
import { Alert, Form, Input, InputNumber, Modal, Select, Switch, Tag, Typography, message } from "antd";
import API from "../../API/API";

const { Text } = Typography;

const TIERS = ["TIER-1", "TIER-2", "TIER-3"];


/**
 * The same normalization the backend applies, repeated here so a domain looks
 * the way it will be stored the moment it is entered rather than after a save.
 */
export const normalizeDomain = (value) => {
    if (typeof value !== "string") {
        return "";
    }

    let domain = value.trim().toLowerCase();
    if (!domain) {
        return "";
    }

    domain = domain.replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
    domain = domain.split("/")[0].split("?")[0].split("#")[0];
    domain = domain.replace(/:\d+$/, "");
    domain = domain.replace(/^www\./, "");
    domain = domain.replace(/\.+$/, "");

    return domain;
};

export const slugifyKey = (value) => String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const DOMAIN_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

const DomainGroupModal = ({ open, group, onClose, onSaved }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [domains, setDomains] = useState([]);
    const [domainError, setDomainError] = useState(null);

    const isEdit = Boolean(group?._id);

    useEffect(() => {
        if (!open) {
            return;
        }

        setDomainError(null);

        if (isEdit) {
            setDomains(group.domains || []);
            form.setFieldsValue({
                name: group.name,
                key: group.key,
                tier: group.tier,
                order: group.order ?? 0,
                visible: group.visible !== false
            });
        } else {
            setDomains([]);
            form.resetFields();
            form.setFieldsValue({ tier: "TIER-1", order: 0, visible: true, key: "" });
        }
    }, [open, group, isEdit, form]);

    // On create the key follows the name; once saved it is frozen, because it is
    // stored on every lead the group has assigned.
    const onNameChange = (event) => {
        if (isEdit) {
            return;
        }
        form.setFieldsValue({ key: slugifyKey(event.target.value) });
    };

    const onDomainsChange = (values) => {
        const normalized = [];
        const rejected = [];

        values.forEach((value) => {
            const domain = normalizeDomain(value);

            if (!domain) {
                return;
            }
            if (!DOMAIN_PATTERN.test(domain)) {
                rejected.push(value);
                return;
            }
            if (!normalized.includes(domain)) {
                normalized.push(domain);
            }
        });

        setDomains(normalized);
        setDomainError(rejected.length ? `Not a valid hostname: ${rejected.join(", ")}` : null);
    };

    const handleSubmit = async () => {
        let values;

        try {
            values = await form.validateFields();
        } catch {
            return;
        }

        if (!domains.length) {
            setDomainError("A domain group needs at least one domain.");
            return;
        }

        setSaving(true);

        try {
            const payload = {
                name: values.name,
                domains,
                order: values.order ?? 0,
                visible: values.visible !== false
            };

            const response = isEdit
                ? await API.updateDomainGroup(group._id, payload)
                : await API.createDomainGroup({ ...payload, key: values.key, tier: values.tier });

            if (response?.success) {
                message.success(response.message || (isEdit ? "Group updated" : "Group created"));
                onSaved?.();
                onClose?.();
            } else {
                message.error(response?.message || "Could not save the group");
            }
        } catch (error) {
            message.error(error?.message || "Could not save the group");
        } finally {
            setSaving(false);
        }
    };

    const frozenHint = useMemo(() => (
        isEdit
            ? "The key and tier are stored on every lead this group has assigned, so neither can change."
            : "The key is generated from the name and cannot be changed later."
    ), [isEdit]);

    return (
        <Modal
            open={open}
            title={isEdit ? `Edit "${group?.name}"` : "Add New Group"}
            okText={isEdit ? "Save" : "Create"}
            onOk={handleSubmit}
            onCancel={onClose}
            confirmLoading={saving}
            destroyOnClose
            width={620}
        >
            <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item
                    name="name"
                    label="Name"
                    rules={[{ required: true, message: "Give the group a name" }]}
                    extra="Shown as the column heading on the Team page and as the lane label on leads."
                >
                    <Input placeholder="e.g. Premium" onChange={onNameChange} maxLength={60} />
                </Form.Item>

                <Form.Item name="key" label="Key" extra={frozenHint}>
                    <Input disabled placeholder="generated from the name" />
                </Form.Item>

                {/*
                  * Every tier routes leads by group membership, so all three are
                  * offered. Frozen once saved: the tier is baked into the round
                  * robin key and into every lead the group has assigned.
                  */}
                <Form.Item
                    name="tier"
                    label="Tier"
                    rules={[{ required: true, message: "Pick a tier" }]}
                >
                    <Select disabled={isEdit} options={TIERS.map((tier) => ({ value: tier, label: tier }))} />
                </Form.Item>

                <Form.Item label="Domains" required help={domainError} validateStatus={domainError ? "error" : ""}>
                    <Select
                        mode="tags"
                        value={domains}
                        onChange={onDomainsChange}
                        tokenSeparators={[",", " ", "\n"]}
                        placeholder="Type a domain and press Enter"
                        open={false}
                        suffixIcon={null}
                        tagRender={({ label, closable, onClose: onTagClose }) => (
                            <Tag color="blue" closable={closable} onClose={onTagClose} style={{ marginInlineEnd: 4 }}>
                                {label}
                            </Tag>
                        )}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Pasted URLs are reduced to the hostname: https://www.example.com/x becomes example.com.
                        A domain can belong to only one group.
                    </Text>
                </Form.Item>

                <Form.Item name="order" label="Order" extra="Lower numbers come first on the Team page.">
                    <InputNumber min={0} style={{ width: 120 }} />
                </Form.Item>

                <Form.Item
                    name="visible"
                    label="Visible on the Team page"
                    valuePropName="checked"
                    extra="A hidden group still routes leads; it just has no column."
                >
                    <Switch />
                </Form.Item>

                {isEdit && (
                    <Alert
                        type="info"
                        showIcon
                        message="Renaming is safe"
                        description="Leads already assigned from this group keep their history. Only the label changes."
                    />
                )}
            </Form>
        </Modal>
    );
};

export default DomainGroupModal;
